/* `/agents` — the org's agents: the draft each one is, and the writes that edit
   it. CONTRACT §11 for the wire.

   In `shared/api/` for the same reason as `apps.ts` and `knowledge.ts`: the app
   shell, the Configure panel and the agents feature all read it, and a feature
   may never import a feature (rule book §1 rule 5).

   THE WIRE → THE APP. Much of an agent has no route behind it yet — evals,
   traces, tasks, memory and the per-agent knowledge folder. `toAgent` is the one
   place the two meet: it takes the row the API returns and fills the rest with
   empty collections, so every agent is a real `Agent` downstream. When a domain
   lands its own routes, it moves out of `toAgent` and into its own module.

   Paths are bare; `client.baseURL` carries `/api/v1`. Every response goes
   through `unwrap`. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";
import { ApiError } from "./errors";
import { modelKeys } from "./models";
import { fastSelectionOf, selectionKey, selectionOf, toFastSelection, toModelChoice, toSelection } from "@/shared/lib/models";

import type {
  Agent,
  AgentCapabilities,
  AgentCreate,
  AgentDeleted,
  AgentPatch,
  AgentPublished,
  AgentRow,
  AgentSecret,
  AgentVersion,
  AgentVersionRow,
  SandboxSize,
  VoiceSettings,
} from "@/shared/types";
import type { ModelSelection } from "@/shared/types/models";

export const agentKeys = {
  all: ["agents"] as const,
  list: () => [...agentKeys.all, "list"] as const,
  detail: (id: string) => [...agentKeys.all, "detail", id] as const,
  versions: (id: string) => [...agentKeys.all, "versions", id] as const,
  sandboxSizes: () => [...agentKeys.all, "sandbox-sizes"] as const,
  secrets: (id: string) => [...agentKeys.all, "secrets", id] as const,
  /** Mutation keys: what `useIsMutating` counts to tell a draft still being written. */
  patch: () => [...agentKeys.all, "patch"] as const,
  publish: () => [...agentKeys.all, "publish"] as const,
};

/* ------------------------------ endpoints -------------------------------- */

export async function listAgents(): Promise<AgentRow[]> {
  return unwrap(await client.get<Envelope<AgentRow[]>>("/agents"));
}

export async function getAgent(id: string): Promise<AgentRow> {
  return unwrap(await client.get<Envelope<AgentRow>>(`/agents/${id}`));
}

/** Creates the draft **and** publishes it as version 1, so it is runnable. */
export async function createAgent(body: AgentCreate): Promise<AgentRow> {
  return unwrap(await client.post<Envelope<AgentRow>>("/agents", body));
}

export async function patchAgent(id: string, body: AgentPatch): Promise<AgentRow> {
  return unwrap(await client.patch<Envelope<AgentRow>>(`/agents/${id}`, body));
}

/** Who approves the agent's skills: any active member. Applies at once, outside versions. */
export async function setAgentOwner(id: string, userId: string): Promise<AgentRow> {
  return unwrap(await client.put<Envelope<AgentRow>>(`/agents/${id}/owner`, { user_id: userId }));
}

export async function deleteAgent(id: string): Promise<AgentDeleted> {
  return unwrap(await client.delete<Envelope<AgentDeleted>>(`/agents/${id}`));
}

/** Freezes the draft as the next version. `created` is false when the server
    answered 200: the draft matched the published version, so nothing was added. */
export async function publishAgent(id: string): Promise<{ published: AgentPublished; created: boolean }> {
  const res = await client.post<Envelope<AgentPublished>>(`/agents/${id}/publish`, {});
  return { published: unwrap(res), created: res.status === 201 };
}

/** The draft back on the size's base image: Definable's environment stops reaching new
    machines once this is published. */
export async function resetEnvironment(id: string): Promise<AgentRow> {
  return unwrap(await client.delete<Envelope<AgentRow>>(`/agents/${id}/environment`));
}

/** Deletes the team machine: the next internal run starts a new one from the published
    environment, and whatever the team left on it is gone. */
export async function resetTeamMachine(id: string): Promise<{ id: string; team_machine: "reset" }> {
  return unwrap(await client.post<Envelope<{ id: string; team_machine: "reset" }>>(`/agents/${id}/team-machine/reset`, {}));
}

/** Names and last four characters only: a value goes in and never comes out. */
export async function listSecrets(id: string): Promise<AgentSecret[]> {
  return unwrap(await client.get<Envelope<AgentSecret[]>>(`/agents/${id}/secrets`));
}

export async function putSecret(id: string, name: string, value: string): Promise<AgentSecret> {
  return unwrap(await client.put<Envelope<AgentSecret>>(`/agents/${id}/secrets/${encodeURIComponent(name)}`, { value }));
}

export async function deleteSecret(id: string, name: string): Promise<{ name: string; status: "deleted" }> {
  return unwrap(await client.delete<Envelope<{ name: string; status: "deleted" }>>(`/agents/${id}/secrets/${encodeURIComponent(name)}`));
}

export async function listAgentVersions(id: string): Promise<AgentVersionRow[]> {
  return unwrap(await client.get<Envelope<AgentVersionRow[]>>(`/agents/${id}/versions`));
}

export async function listSandboxSizes(): Promise<SandboxSize[]> {
  return unwrap(await client.get<Envelope<SandboxSize[]>>("/agents/sandbox-sizes"));
}

/* -------------------------------- hooks ---------------------------------- */

export function useAgents() {
  return useQuery({ queryKey: agentKeys.list(), queryFn: listAgents });
}

export function useAgentVersions(id: string | undefined) {
  return useQuery({
    queryKey: agentKeys.versions(id ?? ""),
    queryFn: () => listAgentVersions(id!),
    enabled: !!id,
  });
}

/** The catalog is a constant in the backend: read once per session. */
export function useSandboxSizes() {
  return useQuery({ queryKey: agentKeys.sandboxSizes(), queryFn: listSandboxSizes, staleTime: Infinity });
}

export function useAgentSecrets(id: string | undefined) {
  return useQuery({
    queryKey: agentKeys.secrets(id ?? ""),
    queryFn: () => listSecrets(id!),
    enabled: !!id,
  });
}

/** A refused model choice: our picture of the workspace's connections is stale. */
const MODEL_REFUSALS = [3060005, 3100007, 1010005, 3060013, 3060014];

/* The list is re-read whatever the answer. After a refusal the screen still holds
   the edit the server turned down, and re-reading is what puts the real draft back. */
function useAgentsMutation<V, T>(
  mutationFn: (vars: V) => Promise<T>,
  { key, meta }: { key?: readonly unknown[]; meta?: Record<string, unknown> } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    mutationKey: key,
    meta,
    onError: (err) => {
      if (err instanceof ApiError && err.code != null && MODEL_REFUSALS.includes(err.code)) {
        qc.invalidateQueries({ queryKey: modelKeys.all });
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: agentKeys.all }),
  });
}

export const useCreateAgent = () => useAgentsMutation(createAgent);

export type AgentPatchVars = { id: string; body: AgentPatch };
export const usePatchAgent = () =>
  useAgentsMutation((v: AgentPatchVars) => patchAgent(v.id, v.body), { key: agentKeys.patch() });

export const useDeleteAgent = () => useAgentsMutation(deleteAgent);

export const useSetAgentOwner = () =>
  useAgentsMutation((v: { id: string; userId: string }) => setAgentOwner(v.id, v.userId));

export const useResetEnvironment = () => useAgentsMutation(resetEnvironment);
export const useResetTeamMachine = () => useAgentsMutation(resetTeamMachine);
export const usePutSecret = () =>
  useAgentsMutation((v: { id: string; name: string; value: string }) => putSecret(v.id, v.name, v.value));
export const useDeleteSecret = () =>
  useAgentsMutation((v: { id: string; name: string }) => deleteSecret(v.id, v.name));

/** Silent: its caller has already saved the draft, and only it can say so in the toast. */
export const usePublishAgent = () => useAgentsMutation(publishAgent, { key: agentKeys.publish(), meta: { silent: true } });

/* ------------------------- the wire → the mockup -------------------------- */

/** An id the server could have issued: a uuid. A stale deep link can hold anything else,
    and the routes that want a uuid are never asked with it. */
export const isUuid = (id: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

/** The seven switches the API owns, and the sandbox settings beside them. */
const flags = (row: AgentRow): AgentCapabilities => ({
  memoryEnabled: row.capabilities.memory,
  subAgentsEnabled: row.capabilities.subagents,
  appsEnabled: row.capabilities.apps,
  mcpEnabled: row.capabilities.mcp,
  sandboxEnabled: row.capabilities.sandbox,
  sandboxSize: row.sandbox_size,
  sandboxShared: !!row.sandbox_shared,
  sandboxEnvironment: row.sandbox_environment ?? null,
  webEnabled: row.capabilities.web,
  voiceEnabled: !!row.capabilities.voice,
});

/** How the agent sounds and hears on a call, from its row. */
const voiceOf = (row: AgentRow): VoiceSettings => ({
  voiceId: row.voice_id ?? null,
  listenModel: row.listen_model ?? null,
  speakModel: row.speak_model ?? null,
});

/** The wire's `capabilities`, from the mockup's. The inverse of `capabilities`,
    and the only shape `PATCH /agents/{id}` accepts. */
export const capabilityPatch = (caps: AgentCapabilities): AgentPatch["capabilities"] => ({
  memory: !!caps.memoryEnabled,
  subagents: !!caps.subAgentsEnabled,
  apps: !!caps.appsEnabled,
  mcp: !!caps.mcpEnabled,
  sandbox: !!caps.sandboxEnabled,
  web: !!caps.webEnabled,
  voice: !!caps.voiceEnabled,
});

/**
 * One API row as the `Agent` the rest of the app already knows how to render.
 *
 * Everything the API does not carry comes back empty rather than invented: a
 * server agent opens with no chats, no memory because it genuinely
 * has none. `version` reads `v1`, `v2`… off `published_version` so the header
 * and the version list agree with what the backend would actually run.
 */
export function toAgent(row: AgentRow): Agent {
  const version = `v${row.published_version ?? 1}`;
  return {
    id: row.id,
    name: row.name,
    hue: row.hue,
    seed: row.seed,
    model: row.model,
    version,
    versions: [{ id: version, label: "Published", when: "", current: true, snapshot: null }],
    connectedApps: [],
    allApps: [],
    capabilities: flags(row),
    instructions: row.instructions ?? "",
    about: row.about ?? "",
    // Temperature has no column: it stays a console-only setting.
    modelSettings: { selection: toSelection(row), fastSelection: toFastSelection(row), fastAuto: row.fast_model_auto ?? true, effort: row.effort ?? null, style: row.style ?? null, model: row.model, temperature: 0.7, maxTokens: row.max_tokens ?? 4096, maxSteps: row.max_steps ?? null },
    voice: voiceOf(row),
    needsApproval: row.needs_approval ?? [],
    guardrails: row.guardrails ?? [],
    evalsBeforePublish: row.evals_before_publish ?? true,
    chats: [],
  };
}

/** The server's history as the rows the settings page renders. There is no
    snapshot to diff or restore: restore has no endpoint yet. */
export const toVersions = (rows: AgentVersionRow[], current: string): AgentVersion[] =>
  rows.map((row) => ({
    id: `v${row.version}`,
    label: row.summary ?? "Published",
    when: new Date(row.published_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    current: `v${row.version}` === current,
    snapshot: null,
  }));

/**
 * A fresh row read onto an agent we already hold.
 *
 * A refetch must not throw away what only the browser knows — the chats, memory
 * and eval runs of this session — so only the fields the API owns are copied
 * back. That is also why this exists instead of calling `toAgent` again: that
 * one is for an agent arriving for the first time, and its empty collections
 * are an honest answer only then.
 */
export function mergeAgent(local: Agent, row: AgentRow): Agent {
  return {
    ...local,
    name: row.name,
    hue: row.hue,
    seed: row.seed,
    model: row.model,
    modelSettings: { ...local.modelSettings, model: row.model, selection: toSelection(row), fastSelection: toFastSelection(row), fastAuto: row.fast_model_auto ?? true, effort: row.effort ?? null, style: row.style ?? null, maxTokens: row.max_tokens ?? local.modelSettings.maxTokens, maxSteps: row.max_steps ?? null },
    voice: voiceOf(row),
    instructions: row.instructions ?? "",
    about: row.about ?? "",
    version: `v${row.published_version ?? 1}`,
    capabilities: { ...local.capabilities, ...flags(row) },
    needsApproval: row.needs_approval ?? [],
    guardrails: row.guardrails ?? [],
    evalsBeforePublish: row.evals_before_publish ?? true,
  };
}

const selectionKeyOrNull = (selection: ModelSelection | null) => (selection ? selectionKey(selection) : null);

/** What changed between an agent and its edit, in the wire's words — `{}` when
    the edit touched nothing the API owns (a memory note). */
export function agentPatch(before: Agent, after: Agent): AgentPatch {
  // The model and its connection travel as a pair, the way the service checks them.
  const patch: AgentPatch = selectionKey(selectionOf(before)) === selectionKey(selectionOf(after)) ? {} : toModelChoice(selectionOf(after));
  // so does the fast model: null removes it and its key
  const fastAfter = fastSelectionOf(after);
  if (selectionKeyOrNull(fastSelectionOf(before)) !== selectionKeyOrNull(fastAfter)) {
    const fast = fastAfter ? toModelChoice(fastAfter) : null;
    patch.fast_model = fast?.model ?? null;
    patch.fast_connection_id = fast?.connection_id ?? null;
  }
  if ((after.modelSettings?.fastAuto ?? true) !== (before.modelSettings?.fastAuto ?? true)) {
    patch.fast_model_auto = after.modelSettings?.fastAuto ?? true;
  }
  if ((after.modelSettings?.effort ?? null) !== (before.modelSettings?.effort ?? null)) {
    patch.effort = after.modelSettings?.effort ?? null;
  }
  if ((after.modelSettings?.style ?? null) !== (before.modelSettings?.style ?? null)) {
    patch.style = after.modelSettings?.style ?? null;
  }
  const voiceFields = { voiceId: "voice_id", listenModel: "listen_model", speakModel: "speak_model" } as const;
  for (const [field, wire] of Object.entries(voiceFields) as [keyof VoiceSettings, (typeof voiceFields)[keyof VoiceSettings]][]) {
    if ((after.voice?.[field] ?? null) !== (before.voice?.[field] ?? null)) patch[wire] = after.voice?.[field] ?? null;
  }
  if (after.hue !== before.hue) patch.hue = after.hue;
  if (after.seed !== before.seed) patch.seed = after.seed;
  if (after.name !== before.name) patch.name = after.name;
  if (after.instructions !== before.instructions) patch.instructions = after.instructions;
  if ((after.about ?? "") !== (before.about ?? "")) patch.about = after.about || null;
  if (after.modelSettings?.maxTokens !== before.modelSettings?.maxTokens) {
    patch.max_tokens = after.modelSettings.maxTokens;
  }
  if ((after.modelSettings?.maxSteps ?? null) !== (before.modelSettings?.maxSteps ?? null)) {
    patch.max_steps = after.modelSettings.maxSteps ?? null;
  }
  if ((after.capabilities.sandboxSize ?? null) !== (before.capabilities.sandboxSize ?? null)) {
    patch.sandbox_size = after.capabilities.sandboxSize ?? null;
  }
  if (!!after.capabilities.sandboxShared !== !!before.capabilities.sandboxShared) {
    patch.sandbox_shared = !!after.capabilities.sandboxShared;
  }
  const caps = capabilityPatch(after.capabilities);
  if (JSON.stringify(caps) !== JSON.stringify(capabilityPatch(before.capabilities))) {
    patch.capabilities = caps;
  }
  if (JSON.stringify(after.needsApproval ?? []) !== JSON.stringify(before.needsApproval ?? [])) {
    patch.needs_approval = after.needsApproval ?? [];
  }
  if (JSON.stringify(after.guardrails ?? []) !== JSON.stringify(before.guardrails ?? [])) {
    patch.guardrails = after.guardrails ?? [];
  }
  if ((after.evalsBeforePublish ?? true) !== (before.evalsBeforePublish ?? true)) {
    patch.evals_before_publish = after.evalsBeforePublish ?? true;
  }
  return patch;
}
